import React, { FC } from 'react';
import Tab from 'react-bootstrap/Tab';
import Container from 'react-bootstrap/cjs/Container';
import { Alert } from 'react-bootstrap';
import Tabs from 'react-bootstrap/cjs/Tabs';
import Badge from 'react-bootstrap/cjs/Badge';
import { GroupedByKindEntityVersions } from './GroupedByKindEntityVersions';
import { GroupedByKindModifiedEntityVersions } from './GroupedByKindModifiedEntityVersions';
import { ExperimentVmTable } from './ExperimentVmTable';
import { YoTabs } from '../../components/yo-tabs/YoTabs';
import { IDistributionDiffVm, IEntityVersionDiffVm, IEntityVersionVm } from '@yakoffice/publisher-types';
import { useGameEnvironmentCssClass } from '../common/useGameEnvironmentCssClass';
import { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import styles from './DiffGroupedView.module.sass';

interface PropTypes {
    distributionDiff: IDistributionDiffVm;
    gameEnvironment: IGameEnvironment;
    allowActions: boolean;
    handleMergeEntityDiffs: (modifiedEntities: IEntityVersionDiffVm[]) => void;
    handleStopEntities: (entities: IEntityVersionVm[]) => void;
    handleCopyPublishEntities: (entities: IEntityVersionVm[]) => void;
}

export const DiffGroupedView: FC<PropTypes> = ({
                                                   distributionDiff,
                                                   gameEnvironment,
                                                   allowActions,
                                                   handleMergeEntityDiffs,
                                                   handleStopEntities,
                                                   handleCopyPublishEntities
                                               }) => {
    const cssClass = useGameEnvironmentCssClass(gameEnvironment);

    return <Container fluid><YoTabs id="diffGroupedTabs">
        <Tab eventKey="entitiesTab"
             className={`${styles.diffCompareTab}`}
             title={<span data-testid="tabEntities">Entities</span>}>

            {(distributionDiff.addedEntities.length === 0 && distributionDiff.modifiedEntities.length === 0 && distributionDiff.deletedEntities.length === 0)
                ? <Alert variant={'success'} className="text-center mt-4">No differences between entities</Alert>
                :
                <Tabs id="entitiesDiff" variant="pills" className="mt-4">
                    {distributionDiff.addedEntities.length > 0 &&
                        <Tab eventKey="addedEntities"
                             title={`Added (${distributionDiff.addedEntities.length})`}>
                            <div data-testid="tabContentAddedEntities">
                                <GroupedByKindEntityVersions className={cssClass.name}
                                                             entityVersions={distributionDiff.addedEntities}
                                                             entitiesTitle="Added Entities"
                                                             allowSelection={allowActions}
                                                             selectedEntityButtonName="Stop Entities"
                                                             handleSelectedEntities={handleStopEntities}
                                />
                            </div>
                        </Tab>
                    }
                    {distributionDiff.deletedEntities.length > 0 &&
                        <Tab eventKey="deletedEntities"
                             title={`Deleted (${distributionDiff.deletedEntities.length})`}>
                            <div data-testid="tabContentDeletedEntities">
                                <GroupedByKindEntityVersions className={cssClass.name}
                                                             entityVersions={distributionDiff.deletedEntities}
                                                             entitiesTitle="Deleted Entities"
                                                             allowSelection={allowActions}
                                                             selectedEntityButtonName="Copy/Publish Entities"
                                                             handleSelectedEntities={handleCopyPublishEntities} />
                            </div>
                        </Tab>
                    }
                    {distributionDiff.modifiedEntities.length > 0 &&
                        <Tab eventKey="modifiedEntities"
                             title={`Modified (${distributionDiff.modifiedEntities.length})`}>
                            <div data-testid="tabContentModifiedEntities">
                                <GroupedByKindModifiedEntityVersions className={cssClass.name}
                                                                     modifiedEntities={distributionDiff.modifiedEntities}
                                                                     allowSelection={allowActions}
                                                                     handleMergeChanges={handleMergeEntityDiffs}
                                />
                            </div>
                        </Tab>
                    }
                </Tabs>
            }
        </Tab>

        <Tab eventKey="experimentsTab"
             className={`${styles.diffCompareTab}`}
             title={<span className="align-content-m h6" data-testid="tabExperiments">Experiments</span>}>
            {(distributionDiff.addedExperiments.length === 0 && distributionDiff.deletedExperiments.length === 0)
                ? <Alert variant={'success'} className="text-center mt-4">No differences between experiments</Alert>

                : <Tabs id="experimentsDiff" variant="pills" className="mt-4">
                    {distributionDiff.addedExperiments.length > 0 &&
                        <Tab eventKey="addedExperiments"
                             title={<span className="h6" data-testid="tabAddedExperiments"><i
                                 className="fas fa-plus" /> Added <Badge
                                 bg="primary">{distributionDiff.addedExperiments.length}</Badge></span>}>

                            {distributionDiff.addedExperiments.length > 0 ?
                                <ExperimentVmTable className={cssClass.name}
                                                   experiments={distributionDiff.addedExperiments}
                                                   title="Added Experiments" />
                                : <div>No additional experiments</div>
                            }

                        </Tab>
                    }
                    {distributionDiff.deletedExperiments.length > 0 &&
                        <Tab eventKey="deletedExperiments"
                             title={<span className="h6" data-testid="tabDeletedExperiments"><i
                                 className="fas fa-minus" /> Deleted <Badge
                                 bg="primary">{distributionDiff.deletedExperiments.length}</Badge></span>}>

                            {distributionDiff.deletedExperiments.length > 0 ?
                                <ExperimentVmTable className={cssClass.name}
                                                   experiments={distributionDiff.deletedExperiments}
                                                   title="Deleted Experiments" />
                                : <div>All experiments are present</div>
                            }

                        </Tab>
                    }
                </Tabs>

            }
        </Tab>
    </YoTabs></Container>;
};
